import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Upload, X, Video, Loader2, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * VideoUpload Component for uploading MP4/WebM videos to S3
 * @param {string} uploadEndpoint - API endpoint for upload (e.g., '/api/upload/site/video')
 * @param {string} deleteEndpoint - API endpoint for delete (e.g., '/api/upload/site/video')
 * @param {string} currentUrl - Current video URL if exists
 * @param {function} onUploadSuccess - Callback with new URL after successful upload
 * @param {function} onDelete - Callback after successful delete
 * @param {string} label - Button label
 * @param {number} maxSize - Max file size in MB (default 50)
 */
const VideoUpload = ({
  uploadEndpoint,
  deleteEndpoint,
  currentUrl,
  onUploadSuccess,
  onDelete,
  label = 'Upload Video',
  maxSize = 50,
}) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only MP4, WebM, and MOV video files are allowed');
      return;
    }

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`File is too large. Maximum size is ${maxSize}MB`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}${uploadEndpoint}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      toast.success('Video uploaded successfully');
      if (onUploadSuccess) {
        onUploadSuccess(response.data.url);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!currentUrl) return;
    
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}${deleteEndpoint}`);
      toast.success('Video deleted successfully');
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete video');
    } finally {
      setDeleting(false);
    }
  };

  // Check if URL is a YouTube link
  const isYouTube = currentUrl?.includes('youtube.com') || currentUrl?.includes('youtu.be');

  return (
    <div className="space-y-3">
      {/* Current Video Preview */}
      {currentUrl && (
        <div className="relative rounded-lg overflow-hidden bg-slate-900">
          {isYouTube ? (
            <div className="aspect-video flex items-center justify-center bg-slate-800 text-white">
              <div className="text-center">
                <Play className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm opacity-75">YouTube Video</p>
                <p className="text-xs opacity-50 mt-1 max-w-xs truncate">{currentUrl}</p>
              </div>
            </div>
          ) : (
            <video 
              src={currentUrl} 
              className="w-full aspect-video object-cover"
              controls
              preload="metadata"
            />
          )}
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="absolute top-2 right-2 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors disabled:opacity-50"
            title="Delete video"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          </button>
        </div>
      )}

      {/* Upload Area */}
      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        
        {!currentUrl ? (
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-slate-50 transition-colors"
          >
            <Video className="h-10 w-10 mx-auto text-slate-400 mb-3" />
            <p className="text-sm font-medium text-slate-700">{label}</p>
            <p className="text-xs text-slate-500 mt-1">MP4, WebM, or MOV up to {maxSize}MB</p>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full"
          >
            <Upload className="h-4 w-4 mr-2" />
            Replace Video
          </Button>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="mt-3">
            <div className="flex items-center gap-2 mb-1">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-slate-600">Uploading... {uploadProgress}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoUpload;
